import { useEffect, useRef } from 'react';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { usePresence } from '@/hooks/usePresence';
import type { StageId } from '@/layout/types';
import type { DisplayNodes, FunnelResult } from '@/model/types';
import { useFunnelStore } from '@/store/useFunnelStore';
import { ChaseStage } from './ChaseStage';
import { ImpactBoxes } from './parts';
import { ReachStage } from './ReachStage';
import { ScreenStage } from './ScreenStage';
import { SourceStage } from './SourceStage';
import { StageIcon } from '../StageIcon';
import { STAGES, STAGE_BY_ID } from './stages';

interface Props {
  result: FunnelResult;
  display: DisplayNodes;
}

/** Keep in step with --drawer-anim in index.css. */
const ANIMATION_MS = 240;

export function StageDrawer({ result, display }: Props) {
  const selectedStage = useFunnelStore((s) => s.selectedStage);
  const baseline = useFunnelStore((s) => s.baseline);
  const status = useFunnelStore((s) => s.status);
  const statusMessage = useFunnelStore((s) => s.statusMessage);
  const closeDrawer = useFunnelStore((s) => s.closeDrawer);
  const openStage = useFunnelStore((s) => s.openStage);
  const resetParams = useFunnelStore((s) => s.resetParams);

  const panelRef = useRef<HTMLElement>(null);
  const open = selectedStage !== null;
  const { mounted, active } = usePresence(open, ANIMATION_MS);

  /* Hold the last stage so the panel still has content to show while it slides
     back out, after the store has already cleared the selection. */
  const lastStage = useRef<StageId | null>(null);
  if (selectedStage) lastStage.current = selectedStage;
  const stageId = selectedStage ?? lastStage.current;

  /* Gate on `mounted` too: the first render after opening still returns null
     while usePresence catches up, so an ungated trap would install against a
     ref that is not in the DOM yet and never take hold. */
  useFocusTrap(panelRef, open && mounted);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeDrawer();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, closeDrawer]);

  if (!mounted || !stageId) return null;
  const meta = STAGE_BY_ID.get(stageId);
  if (!meta) return null;

  /* Switching tabs keeps the original baseline, so the impact boxes stay
     anchored to when the session of edits began. */
  const goTo = (id: StageId) =>
    openStage(id, {
      completed: baseline?.completed ?? result.nodes.completed,
      screeningRate: baseline?.screeningRate ?? result.rates.screeningRate,
    });

  const openClass = active ? ' is-open' : '';

  return (
    <>
      <div className={`drawer-backdrop${openClass}`} onClick={closeDrawer} aria-hidden="true" />

      <aside
        ref={panelRef}
        className={`editor-drawer${openClass}`}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
      >
        <header className="drawer-header">
          <div>
            <span className="drawer-kicker">Edit stage</span>
            <h2 id="drawer-title">
              <StageIcon stage={stageId} size={16} />
              {meta.title}
            </h2>
          </div>
          <button
            type="button"
            className="drawer-close"
            onClick={closeDrawer}
            aria-label="Close stage editor"
          >
            ×
          </button>
        </header>

        <nav className="drawer-nav" aria-label="Jump to stage">
          {STAGES.map((stage) => (
            <button
              key={stage.id}
              type="button"
              aria-current={stage.id === stageId}
              onClick={() => goTo(stage.id)}
            >
              <StageIcon stage={stage.id} />
              {stage.short}
            </button>
          ))}
        </nav>

        <div className="drawer-body">
          <p className="drawer-intro">{meta.intro}</p>

          {stageId === 'source' && <SourceStage result={result} display={display} />}
          {stageId === 'reach' && (
            <ReachStage result={result} display={display} onEditSources={() => goTo('source')} />
          )}
          {stageId === 'screen' && (
            <ScreenStage result={result} display={display} onEditSources={() => goTo('source')} />
          )}
          {stageId === 'chase' && <ChaseStage result={result} display={display} />}

          {stageId !== 'source' && stageId !== 'screen' && (
            <ImpactBoxes
              baseline={baseline}
              completed={result.nodes.completed}
              screeningRate={result.rates.screeningRate}
            />
          )}

          <button type="button" className="drawer-reset" onClick={resetParams}>
            Reset all assumptions to defaults
          </button>

          {status === 'error' && (
            <p className="drawer-status is-error" role="status">
              {statusMessage ?? 'Could not save.'} Your edits are still applied on screen.
            </p>
          )}
          {status === 'saved' && (
            <p className="drawer-status" role="status">
              Saved.
            </p>
          )}
        </div>
      </aside>
    </>
  );
}
